import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Fab,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PlayArrow,
  Refresh,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import {
  RecurringTask,
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  triggerTask,
} from '../api/client';
import TaskDialog from './TaskDialog';

const DAYS_OF_WEEK = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const formatRecurrence = (type: string, day: number | null): string => {
  switch (type) {
    case 'daily':
      return 'Quotidien';
    case 'weekly':
      return day !== null ? `Chaque ${DAYS_OF_WEEK[day]}` : 'Hebdomadaire';
    case 'monthly':
      return day !== null ? `Le ${day} de chaque mois` : 'Mensuel';
    default:
      return type;
  }
};

const formatDate = (date: string | null): string => {
  if (!date) return '-';
  return dayjs(date).format('DD/MM/YYYY HH:mm');
};

const formatDateOnly = (date: string | null): string => {
  if (!date) return '-';
  return dayjs(date).format('DD/MM/YYYY');
};

const TaskTable: React.FC = () => {
  const [tasks, setTasks] = useState<RecurringTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<RecurringTask | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<RecurringTask | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTasks();
      setTasks(response.data);
    } catch (err) {
      showSnackbar('Erreur lors du chargement des taches', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCreate = () => {
    setEditingTask(null);
    setDialogOpen(true);
  };

  const handleEdit = (task: RecurringTask) => {
    setEditingTask(task);
    setDialogOpen(true);
  };

  const handleDeleteClick = (task: RecurringTask) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;

    try {
      await deleteTask(taskToDelete.id);
      showSnackbar('Tache supprimee avec succes', 'success');
      loadTasks();
    } catch (err) {
      showSnackbar('Erreur lors de la suppression', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const handleTrigger = async (task: RecurringTask) => {
    try {
      await triggerTask(task.id);
      showSnackbar(`Tache "${task.name}" creee dans Asana`, 'success');
      loadTasks();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      const message = error.response?.data?.detail || 'Erreur lors de la creation dans Asana';
      showSnackbar(message, 'error');
    }
  };

  const handleSave = async (taskData: Omit<RecurringTask, 'id' | 'created_at' | 'last_success' | 'next_run'>) => {
    try {
      if (editingTask) {
        await updateTask(editingTask.id, taskData);
        showSnackbar('Tache modifiee avec succes', 'success');
      } else {
        await createTask(taskData);
        showSnackbar('Tache creee avec succes', 'success');
      }
      setDialogOpen(false);
      loadTasks();
    } catch (err) {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const columns: GridColDef<RecurringTask>[] = [
    {
      field: 'name',
      headerName: 'Nom',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'recurrence',
      headerName: 'Recurrence',
      width: 180,
      renderCell: (params: GridRenderCellParams<RecurringTask>) => (
        <Chip
          label={formatRecurrence(params.row.recurrence_type, params.row.recurrence_day)}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'project_name',
      headerName: 'Projet',
      width: 150,
      renderCell: (params: GridRenderCellParams<RecurringTask>) => params.row.project_name || '-',
    },
    {
      field: 'section_name',
      headerName: 'Section',
      width: 130,
      renderCell: (params: GridRenderCellParams<RecurringTask>) => params.row.section_name || '-',
    },
    {
      field: 'assignee_name',
      headerName: 'Assigne',
      width: 130,
      renderCell: (params: GridRenderCellParams<RecurringTask>) => params.row.assignee_name || '-',
    },
    {
      field: 'due_date',
      headerName: 'Echeance',
      width: 120,
      renderCell: (params: GridRenderCellParams<RecurringTask>) => formatDateOnly(params.row.due_date),
    },
    {
      field: 'created_at',
      headerName: 'Cree le',
      width: 150,
      renderCell: (params: GridRenderCellParams<RecurringTask>) => formatDate(params.row.created_at),
    },
    {
      field: 'last_success',
      headerName: 'Dernier succes',
      width: 150,
      renderCell: (params: GridRenderCellParams<RecurringTask>) => {
        const date = params.row.last_success;
        if (!date) return <Typography variant="body2" color="text.secondary">-</Typography>;
        return (
          <Chip
            label={formatDate(date)}
            size="small"
            color="success"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'next_run',
      headerName: 'Prochaine exec.',
      width: 150,
      renderCell: (params: GridRenderCellParams<RecurringTask>) => formatDate(params.row.next_run),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<RecurringTask>) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Executer maintenant">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleTrigger(params.row)}
            >
              <PlayArrow />
            </IconButton>
          </Tooltip>
          <Tooltip title="Modifier">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(params.row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Supprimer">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Taches Recurrentes</Typography>
        <Tooltip title="Rafraichir">
          <IconButton onClick={loadTasks} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={tasks}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
            },
          }}
        />
      </Box>

      <Fab
        color="primary"
        aria-label="add"
        onClick={handleCreate}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
      >
        <Add />
      </Fab>

      <TaskDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        task={editingTask}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Etes-vous sur de vouloir supprimer la tache "{taskToDelete?.name}" ?
            Cette action est irreversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TaskTable;
